import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";

const QUESTIONS = [
  "Who were the women of Angkor Wat?",
  "Why are they immortalized with the most prominent placement in the largest temples the Khmer ever built?",
  "What did these women mean to the Khmer rulers, priests and people?",
  "How does the Cambodian dance tradition relate to the women of Angkor Wat?",
  "Do the women of Angkor Wat embody information important to us in modern times?",
];

const STORY = [
  "In the 12th century AD, the Khmer Empire ruled most of what is now Southeast Asia. As Europe struggled through the Dark Ages, King Suryavarman II built the massive edifice of Angkor Wat at the height of his empire's glory.",
  "But within 200 years, the powerful Khmer civilization mysteriously collapsed. Aside from limited temple inscriptions, no written records survived its demise. Dense jungle swallowed the magnificent temples, and Western scholars never even learned that the great Khmer race had existed.",
  "In the 19th century, French explorers rediscovered the ruins, initiating 150 years of intense scholarship that continues today. Yet we believe they missed the most important key to the puzzle — hidden in plain sight.",
  "People worldwide instantly recognize Angkor Wat. Few realize that this temple protects an extraordinary treasure unlike any building on Earth: a royal portrait gallery of 1,795 women realistically rendered in stone. To this day, no one knows the identity of a single one of them.",
];

const AREAS = [
  ["Devata Research", "The Angkor Wat Devata Inventory, the database project, and facial-pattern recognition work with Michigan State University."],
  ["Cambodian Dance", "The sacred female dance tradition passed teacher to teacher — from King Sisowath's dancers to the troupes reviving it today."],
  ["Devata Temples", "Meeting the women of Angkor face to face, with mapped photo galleries across Cambodia, Thailand, Laos and India."],
  ["Women's History", "Recognizing the significance of women who populate the world's largest religious structure — and women everywhere."],
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1750850115672-b03c415db864?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxBbmdrb3IlMjBXYXQlMjBhcHNhcmElMjBkZXZhdGElMjBzdG9uZSUyMGNhcnZpbmd8ZW58MHx8fHwxNzkxMzEyODU4fDA&ixlib=rb-4.1.0&q=85";

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <section className="relative pt-36 lg:pt-44 pb-16 lg:pb-20 bg-[var(--surface)] border-b border-[var(--border-c)]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs uppercase tracking-[0.28em] text-[var(--gold)]"
            >
              About the Archive
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.08 }}
              className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-[var(--text)]"
            >
              Who were the women of Angkor Wat?
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.14 }}
              className="mt-6 font-sans text-base sm:text-lg leading-relaxed text-[var(--text-2)] max-w-xl"
            >
              Devata.org and DatAsia together ask a question scholars overlooked
              for 150 years — and work to give these 1,795 goddesses their names,
              their history and their due.
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="overflow-hidden rounded-sm deckle aspect-[4/5]">
              <img src={HERO_IMG} alt="Devata carving at Angkor Wat" className="h-full w-full object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-6 sm:px-10">
          {STORY.map((p, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <p
                className={`font-sans text-[17px] leading-[1.95] text-[var(--text)] ${
                  i === 0
                    ? "first-letter:font-serif first-letter:text-7xl first-letter:font-semibold first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:leading-[0.75] first-letter:text-[var(--terracotta)]"
                    : "mt-6"
                }`}
              >
                {p}
              </p>
            </Reveal>
          ))}

          <Reveal>
            <blockquote className="mt-10 border-l-2 border-[var(--gold)] pl-6 font-serif italic text-2xl sm:text-3xl leading-snug text-[var(--text)]">
              For 150 years scholars dismissed the women as mere decoration. Our
              research suggests they may have been the driving force behind the
              civilization itself.
            </blockquote>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-[var(--surface)] border-y border-[var(--border-c)]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <h2 className="font-serif text-3xl sm:text-4xl text-[var(--text)] max-w-2xl">
            The questions that drive us
          </h2>
          <div className="mt-10 grid md:grid-cols-5 gap-px bg-[var(--border-c)] rounded-lg overflow-hidden border border-[var(--border-c)]">
            {QUESTIONS.map((q, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="h-full bg-[var(--bg)] p-6">
                  <span className="font-mono text-[10px] text-[var(--gold)]">0{i + 1}</span>
                  <p className="mt-3 font-serif text-xl leading-snug text-[var(--text)]">{q}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 grid sm:grid-cols-2 gap-x-10 gap-y-10">
            {AREAS.map(([title, text], i) => (
              <Reveal key={title} delay={i * 0.06}>
                <div className="border-t border-[var(--border-c)] pt-6">
                  <h3 className="font-serif text-2xl text-[var(--text)]">{title}</h3>
                  <p className="mt-2 font-sans text-sm leading-relaxed text-[var(--text-2)]">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 text-center">
          <p className="font-hand text-3xl text-[var(--terracotta)] -rotate-1">
            Use your eyes, your heart and your mind.
          </p>
          <p className="mt-6 font-sans text-base leading-relaxed text-[var(--text-2)]">
            These ancient women have much to teach us about the rich history of
            Asia. Join us in celebrating the Khmer civilization — past, present
            and future.
          </p>
          <p className="mt-8 font-serif text-2xl text-[var(--text)]">Kent Davis</p>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--text-2)]">
            Project Coordinator · DatAsia &amp; Devata.org
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link
              to="/writings"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--ink)] text-[var(--bg)] px-7 py-3.5 font-sans text-sm font-semibold hover:bg-[var(--terracotta)] transition-colors duration-300"
            >
              Read the Writings <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/books"
              className="inline-flex items-center gap-2 rounded-full border border-[var(--border-c)] text-[var(--text)] px-7 py-3.5 font-sans text-sm font-semibold hover:border-[var(--gold)] transition-colors duration-300"
            >
              Browse the Books
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
